import {
    Button,
    ButtonGroup,
    Content,
    Flex,
    Footer,
    Heading,
    Link,
    Modal,
    Paragraph,
    Radio,
    RadioGroup
} from "@hopper-ui/components";
import { reloadPage } from "@sg-protect/core";
import config, { type Region } from "@sg-protect/runtime-config";
import { MixpanelProperty } from "../tracking/mixpanelProperty.ts";
import { useTrackModalState, useTrackRadioChanged } from "../tracking/useTrackEvent.ts";
import { Trans } from "@sg-protect/federation-runtime/i18n";
import { useShellTranslation } from "../i18n/i18next.ts";
import { usePrevious } from "@uidotdev/usehooks";
import { useEffect, useMemo, useState } from "react";
import { type UIMatch, useMatches } from "react-router";
import { REGION_LABELS, REGION_LABEL_KEYS } from "../region-picker/regionLabels.ts";

const MODAL_NAME = "Region Selection Modal";

export const RegionSelectionModal = () => {
    const { t, i18n } = useShellTranslation();
    const [selectedRegion, setSelectedRegion] = useState<Region>("na");
    const [hasConfirmed, setHasConfirmed] = useState(false);
    const matches = useMatches();
    const trackRadioChanged = useTrackRadioChanged();
    const [trackModalOpened, trackModalClosed] = useTrackModalState();

    const shouldShowModal = useMemo(() => {
        if (hasConfirmed) {
            return false;
        }

        const isPublicRoute = matches.some((match): match is UIMatch & { handle: { layout: "public" } } => {
            if (!match.handle || typeof match.handle !== "object") {
                return false;
            }

            return "layout" in match.handle && match.handle.layout === "public";
        });

        return config.storedRegion === null && !isPublicRoute;
    }, [matches, hasConfirmed]);

    const prevShouldShowModal = usePrevious(shouldShowModal);

    useEffect(() => {
        if (shouldShowModal && !prevShouldShowModal) {
            trackModalOpened(MODAL_NAME);
        }
    }, [shouldShowModal, prevShouldShowModal, trackModalOpened]);

    const handleRegionChange = (value: string) => {
        const region = value as Region;

        setSelectedRegion(region);
        trackRadioChanged("Region", REGION_LABELS[region], MODAL_NAME);
    };

    const handleConfirm = () => {
        trackModalClosed(
            MODAL_NAME,
            "Confirm",
            {
                [MixpanelProperty.Values]: REGION_LABELS[selectedRegion]
            },
            {
                // keepAlive ensures the tracking request completes before the page hard-refreshes after a region change.
                keepAlive: true
            }
        );

        config.switch(selectedRegion);
        setHasConfirmed(true);

        if (selectedRegion !== config.region) {
            reloadPage();
        }
    };

    return (
        <Modal isOpen={shouldShowModal} isDismissable={false} size="sm" data-public>
            <Heading>{t("RegionSelectionModal.title")}</Heading>

            <Content marginTop="stack-md" marginBottom="stack-xl">
                <Flex direction="column" gap="stack-lg">
                    <Paragraph color="neutral-weak" size="sm" marginBottom="stack-md">
                        <Trans
                            i18n={i18n}
                            ns="shell"
                            i18nKey="RegionSelectionModal.description"
                            components={{
                                learnMoreLink: (
                                    <Link
                                        href="https://help.sharegate.com/en/articles/12630559"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    />
                                )
                            }}
                        />
                    </Paragraph>

                    <RadioGroup
                        value={selectedRegion}
                        onChange={handleRegionChange}
                        aria-label={t("RegionSelectionModal.regionGroup")}
                    >
                        {config.regions.map(region => (
                            <Radio value={region} key={region} size="sm">
                                {t(REGION_LABEL_KEYS[region])}
                            </Radio>
                        ))}
                    </RadioGroup>
                </Flex>
            </Content>

            <Footer>
                <ButtonGroup width="100%" align="end">
                    <Button onPress={handleConfirm} variant="primary" data-public>
                        {t("RegionSelectionModal.confirm")}
                    </Button>
                </ButtonGroup>
            </Footer>
        </Modal>
    );
};
